import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { SvgIconComponent } from "angular-svg-icon";
import * as Highcharts from "highcharts";
import { HighchartsChartModule } from "highcharts-angular";
import { NgClickOutsideDirective } from "ng-click-outside2";
import { Subject, debounceTime } from "rxjs";

import { isPlatformBrowser } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  PLATFORM_ID,
  Resource,
  computed,
  effect,
  inject,
  input,
  model,
  signal,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";

import { DEBOUNCE_TIME_IN_MS } from "../../../app/constants";
import { extendHighcharts } from "../../../helpers/highcharts";
import { AnalyticsService } from "../../../services/analytics.service";
import { AnalyticsAction, LineChartInteraction } from "../../../services/analytics.types";
import { ExtendedChartComponent } from "../../models/base/extendedchartcomponent";
import {
  DEFAULT_END_DATE,
  DEFAULT_START_DATE,
  DatePicker,
  PresetPeriod,
} from "./components/date-picker/date-picker.component";
import { ChartCustomization, getChartInfo } from "./line-chart-configuration";

@Component({
  selector: "line-chart",
  imports: [DatePicker, FormsModule, HighchartsChartModule, NgClickOutsideDirective, SvgIconComponent, TranslatePipe],
  templateUrl: "./line-chart.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./line-chart.component.scss",
})
export class LineChart extends ExtendedChartComponent implements ChartCustomization {
  public readonly resource = input.required<Resource<LineChartLines>>();
  public readonly metadata = input<ChartMetadata>({});
  public readonly dateRange = model<[Date, Date]>([DEFAULT_START_DATE, DEFAULT_END_DATE]);
  public readonly customization = input<ChartCustomization>(this);
  public readonly defaultPeriod = input<PresetPeriod>("1M");
  public readonly viewMode = input<"chart" | "custom">("chart");
  protected readonly lines = computed(() => this.resource().value());
  protected readonly loading = computed(() => this.resource().isLoading());
  protected readonly chartViewMode = computed(() => this.viewMode() === "chart");

  protected readonly legendItems = computed(() => Object.entries<LineMetadata>(this.metadata()));
  protected readonly exportOverflowVisible = signal(false);

  /** High Chart vars */
  protected readonly highcharts: typeof Highcharts = Highcharts;
  protected readonly chartOptions = computed(() => getChartInfo(this, this.customization()));

  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = computed(() => isPlatformBrowser(this.platformId));
  private readonly translate = inject(TranslateService);
  private readonly analytics = inject(AnalyticsService);

  private readonly dateInspection$ = new Subject<void>();

  private readonly _clearChartOnHide = effect(() => {
    if (!this.chartViewMode()) this._chartRef.set(undefined);
  });

  private readonly _updateChart = effect(() => {
    const chartRef = this._chartRef();
    if (!chartRef) return;
    if (this.loading()) {
      chartRef.reflow();
      chartRef.showLoading(" ");
      chartRef.hideNoData();
      return; // do not update the chart to retain the old data, which is visually appealing
    }
    updateChart(chartRef, this.lines(), this.metadata(), this._noDataText());
  });

  private readonly _noDataText = computed(
    () => this.customization().noDataText ?? this.translate.instant("Chart.TrafficGraph.NoDataFound"),
  );

  constructor() {
    super();

    if (this.isBrowser()) {
      extendHighcharts(this.highcharts);
    }

    // debounced to prevent a deluge of date change events
    this.dateInspection$.pipe(debounceTime(DEBOUNCE_TIME_IN_MS), takeUntilDestroyed()).subscribe(() => {
      this.analytics.trackCurrentFeature(AnalyticsAction.InspectLineChart, LineChartInteraction.DateChange);
    });
  }

  public selectDate(range: [Date, Date]) {
    this.dateRange.set(range);
    this.dateInspection$.next();
  }

  public formatPoint(series: string, y: number) {
    const v = y.toFixed(2);
    const label = this.metadata()[series].label;
    return `<span style="color: ${this.metadata()[series].color}">\u25CF</span> ${label}: <b>${v}</b><br/>`;
  }

  protected toggleExportOptions() {
    this.exportOverflowVisible.update((visible) => !visible);
  }

  protected onClickedOutside() {
    this.exportOverflowVisible.set(false);
  }

  protected exportAsPNG() {
    this.chartRef?.exportChartLocal({ type: "image/png" });
  }

  protected exportAsJPG() {
    this.chartRef?.exportChartLocal({ type: "image/jpeg" });
  }

  protected exportAsCSV() {
    this.chartRef?.downloadCSV();
  }

  protected setChartInstance(chart: Highcharts.Chart) {
    // Defer one frame so the browser lays out the flex container before the
    // _updateChart effect fires and calls reflow() — without this the chart
    // sizes itself to a stale (narrower) width.
    requestAnimationFrame(() => {
      this._chartRef.set(chart);
    });
  }
}

function updateChart(chartRef: Highcharts.Chart, lines: LineChartLines, metadata: ChartMetadata, noDataText: string) {
  chartRef.showLoading(" ");

  const seriesKeys = Object.keys(lines);
  const seriesKeySet = new Set(seriesKeys);

  seriesKeys.forEach((serie, index) => {
    const data = lines[serie] as unknown as (number | null)[][];
    const color = metadata[serie]?.color ?? "black";
    const existing = chartRef.series.find((s) => s.name === serie);
    if (existing) {
      // Update in place so the chart animates the line changing to the new values
      existing.setData(data, false, false, false);
    } else {
      // Support for adding new data if the line doesn't exist yet.
      // The explicit zIndex preserves insertion order as paint order; without
      // it, a series that starts hidden gets its SVG group created only when
      // shown, which would put it on top of everything else.
      const visible = metadata[serie]?.visible ?? true;
      chartRef.addSeries({ name: serie, data, type: "line", color, visible, zIndex: index }, false, false);
    }
  });

  for (let i = chartRef.series.length - 1; i >= 0; i--) {
    if (!seriesKeySet.has(chartRef.series[i].name)) {
      chartRef.series[i].remove(false);
    }
  }

  const hasData = seriesKeys.some((k) => (lines[k] as unknown as unknown[]).length > 0);

  chartRef.redraw(true);
  chartRef.hideLoading();

  if (!hasData) {
    chartRef.hideNoData();
    chartRef.showNoData(noDataText);
  }

  chartRef.zoom();
}

export type LineChartLine = [timestampMs: number, value: number | null][];
export type LineChartLines = Record<string, LineChartLine>;
export interface LineMetadata {
  color: string;
  label: string;
  /** When false, the series starts hidden; it stays toggleable via the legend. */
  visible?: boolean;
}
export type ChartMetadata = Record<string, LineMetadata>;
